import { useEffect, useMemo, useRef } from 'react';

import {
  SetURLSearchParams,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

const pulseElement = (el: HTMLElement) => {
  el.classList.remove('pulse-border');
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  el.offsetWidth;
  el.classList.add('pulse-border');
};

const cleanUpParams = (searchParams: URLSearchParams, restricted: string[]) => {
  const next = new URLSearchParams(searchParams);
  [...next.keys()].forEach((key) => {
    if (!restricted.includes(key)) next.delete(key);
  });
  return next;
};

export const useScrollToFromUrl = (setSearchParams: SetURLSearchParams) => {
  const navType = useNavigationType();
  const location = useLocation();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const FORBIDDEN_TO_CLEANUP_PARAMS = ['tab'];

  const cleanedRef = useRef(false);
  const suppressRootScrollOnceRef = useRef(false);
  const prevPathnameRef = useRef(location.pathname);

  const scrollTarget = searchParams.get('scrollTo');
  const pulseTarget = searchParams.get('pulse');
  const withCleanUp = searchParams.get('cleanup') === 'true';

  useEffect(() => {
    const prevPathname = prevPathnameRef.current;

    if (
      prevPathname !== location.pathname &&
      !scrollTarget &&
      !pulseTarget &&
      !suppressRootScrollOnceRef.current &&
      navType !== 'POP'
    ) {
      document.getElementById('root')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }

    prevPathnameRef.current = location.pathname;
  }, [
    location.pathname,
    location.search,
    navType,
    scrollTarget,
    pulseTarget,
    searchParams,
  ]);

  useEffect(() => {
    const hasScrollBehavior = Boolean(scrollTarget || pulseTarget);

    const finishCleanup = () => {
      if (withCleanUp && !cleanedRef.current) {
        cleanedRef.current = true;
        suppressRootScrollOnceRef.current = true;

        setTimeout(() => {
          const prepared = cleanUpParams(
            searchParams,
            FORBIDDEN_TO_CLEANUP_PARAMS,
          );
          setSearchParams(prepared, { replace: true });
        }, 250);
      }
    };

    if (!hasScrollBehavior) {
      if (suppressRootScrollOnceRef.current) {
        suppressRootScrollOnceRef.current = false;
      }

      if (withCleanUp) {
        finishCleanup();
      }

      return;
    }

    cleanedRef.current = false;

    const scrollOpts: ScrollIntoViewOptions = {
      behavior: 'smooth',
      block: 'center',
    };

    const run = (
      scrollEl?: HTMLElement | null,
      pulseEl?: HTMLElement | null,
    ) => {
      requestAnimationFrame(() => {
        if (scrollEl) {
          scrollEl.scrollIntoView(scrollOpts);
        }

        if (pulseEl) {
          pulseElement(pulseEl);
        }

        finishCleanup();
      });
    };

    const tryNow = () => {
      const scrollEl = scrollTarget
        ? document.getElementById(scrollTarget)
        : null;

      const pulseEl = pulseTarget ? document.getElementById(pulseTarget) : null;

      const canRunScroll = !scrollTarget || Boolean(scrollEl);
      const canRunPulse = !pulseTarget || Boolean(pulseEl);

      if (!canRunScroll || !canRunPulse) return false;

      run(scrollEl, pulseEl);
      return true;
    };

    if (tryNow()) return;

    const root = document.getElementById('root') ?? document.body;

    const observer = new MutationObserver(() => {
      if (tryNow()) observer.disconnect();
    });

    observer.observe(root, { childList: true, subtree: true });

    const timeoutId = window.setTimeout(() => observer.disconnect(), 10000);

    return () => {
      window.clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [scrollTarget, pulseTarget, withCleanUp, searchParams, setSearchParams]);
};
